import React, { Suspense, useEffect, useMemo } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Outlet,
  Route,
  RouterProvider,
  useLocation,
  useNavigate,
} from 'react-router';

import enTranslations from '@shopify/polaris/locales/en.json';
import { AppProvider as PolarisProvider, Frame } from '@shopify/polaris';
import { createInstance, OptimizelyProvider } from '@optimizely/react-sdk';
import { ErrorBoundary } from 'react-error-boundary';
import { handleGeneralError } from 'utils/errorHandler';
import ErrorComponent from 'components/ErrorComponent/ErrorComponent';
import ToastNotification from 'components/ToastNotification/ToastNotification';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import PolarisLink from './PolarisLink';
import GoogleTagManager from './GoogleTagManager';
import OAuthRedirect from './auth/OAuthRedirect';
import Install from './install/Install';

import useShop from './hooks/useShop';
import LoadingPage from './auth/LoadingPage';
import useSession, {
  isAccountCreated,
  isAccountSessionLoaded,
} from './hooks/useSession';
import { PlanType } from './utils/plans';
import useGetCapabilities, { Capability } from './hooks/useGetCapabilities';

import '@shopify/polaris/build/esm/styles.css';
import './style.css';
import { FinaliseChargeProvider } from './hooks/useFinaliseCharge';
import { IntercomProvider, useIntercom } from 'react-use-intercom';
import { getEnvironment, getIntercomAppId, getMixPanelID } from './constants';
import {
  getPlatform,
  getShopDomain,
  ShopifyWrapper,
  WooWrapper,
} from './containers/AppBridge';
import ContextualSaveBar from 'components/ContextualSaveBar';
import ConnectedLegacyPlanRedirectRoutes from 'components/LegacyPlanRedirectRoutes/LegacyPlanRedirectRoutes';
import mixpanel from 'mixpanel-browser';
import * as Sentry from '@sentry/react';
import { CaptureConsole } from '@sentry/integrations';
import {
  ONBOARDING_PATH,
  PLAN_PATH,
  SELF_CREATOR_DETAILS_PATH,
  DASHBOARD_PATH,
  SETTINGS_PATH,
  CUSTOMIZATIONS_CERTIFICATE_CUSTOMIZATION_PATH,
  SETTINGS_EMAIL_GLOBAL_PATH,
  CUSTOMIZATIONS_EMAIL_COA_PATH,
  CUSTOMIZATIONS_EMAIL_NFT_PATH,
  CUSTOMIZATIONS_EMAIL_VIEW_NFT_PATH,
  ROYALTY_SPLITS_PATH,
  CREATORS_PATH,
  TOKEN_GATES_PATH,
  PRINTS_PATH,
  CONTRACTS_PATH,
  INSTALL_PATH,
  REDIRECT_PATH,
} from 'routes';

const AdminLink = React.lazy(() => import('./pages/AdminLink'));
const CertificateList = React.lazy(() => import('./pages/CertificateList'));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const ConnectedPricingPage = React.lazy(
  () => import('./pages/ConnectedPricingPage')
);
const ContractsListPage = React.lazy(() => import('./pages/ContractsListPage'));
const CreateContractsFormPage = React.lazy(
  () => import('./pages/CreateContractsFormPage')
);
const CreateDropPage = React.lazy(
  () => import('./pages/DropFormPage/CreateDropPage')
);
const CreatorsPage = React.lazy(() => import('./pages/CreatorsPage'));
const CustomizationSettingsPage = React.lazy(() =>
  import('./pages/SettingsPage').then((module) => ({
    default: module.CustomizationSettingsPage,
  }))
);
const DropPageLoader = React.lazy(() =>
  import('./pages/DropPage/DropPageLoader').then((module) => ({
    default: module.DropPageLoader,
  }))
);
const EditContractsFormPage = React.lazy(
  () => import('./pages/EditContractsFormPage')
);
const EditDropPage = React.lazy(
  () => import('./pages/DropFormPage/EditDropPage')
);
const EditPrintFormPage = React.lazy(() => import('./pages/EditPrintFormPage'));
const EditTokenGateFormPage = React.lazy(
  () => import('./pages/EditTokenGateFormPage')
);
const EmailGlobalCustomization = React.lazy(
  () => import('./pages/SettingsPage/EmailGlobalCustomization')
);
const FixSubscriptionPage = React.lazy(() =>
  import('./pages/FixSubscription/FixSubscriptionPage').then((module) => ({
    default: module.FixSubscriptionPage,
  }))
);
const MetafieldFormLoader = React.lazy(
  () => import('./pages/ProductForm/MetafieldFormLoader')
);
const OnboardingPage = React.lazy(() => import('./pages/OnboardingPage'));
const PageNotFound = React.lazy(
  () => import('./pages/PageNotFound/PageNotFound')
);
const PrintsFormPage = React.lazy(() => import('./pages/PrintsFormPage'));
const PrintsPage = React.lazy(() => import('./pages/PrintsPage'));
const ReportsPageLoader = React.lazy(() =>
  import('./pages/ReportsPage/ReportsPageLoader').then((module) => ({
    default: module.ReportsPageLoader,
  }))
);
const ReportPageLoader = React.lazy(() =>
  import('./components/ReportListItem/ReportPageLoader').then((module) => ({
    default: module.ReportPageLoader,
  }))
);
const RoyaltySplitPageCreateLoader = React.lazy(() =>
  import('./pages/RoyaltySplit/RoyaltySplitPageCreateLoader').then(
    (module) => ({
      default: module.RoyaltySplitPageCreateLoader,
    })
  )
);
const RoyaltySplitPageEditLoader = React.lazy(
  () => import('./pages/RoyaltySplit/RoyaltySplitPageEditLoader')
);
const RoyaltySplitPageLoader = React.lazy(() =>
  import('./pages/RoyaltySplit/RoyaltySplitPageLoader').then((module) => ({
    default: module.RoyaltySplitPageLoader,
  }))
);
const SelfCreatorDetailsPage = React.lazy(
  () => import('./pages/OnboardingPage/SelfCreatorDetailsPage')
);
const SettingsPage = React.lazy(() => import('./pages/SettingsPage'));
const TokenGateFormPage = React.lazy(() => import('./pages/TokenGateFormPage'));
const TokenGatePage = React.lazy(() => import('./pages/TokenGatePage'));

const CertificateEmailPage = React.lazy(() =>
  import('./pages/SettingsPage/AdvancedEmailPage').then((module) => ({
    default: () => <module.default type={module.TestEmailType.CERTIFICATE} />,
  }))
);
const NftClaimEmailPage = React.lazy(() =>
  import('./pages/SettingsPage/AdvancedEmailPage').then((module) => ({
    default: () => <module.default type={module.TestEmailType.NFT_CLAIM} />,
  }))
);
const NftMintedEmailPage = React.lazy(() =>
  import('./pages/SettingsPage/AdvancedEmailPage').then((module) => ({
    default: () => <module.default type={module.TestEmailType.NFT_MINTED} />,
  }))
);

// Add this type declaration at the top of the file, after the imports
declare global {
  interface Window {
    shopify?: {
      config?: {
        version: string;
      };
      toast?: {
        show: (message: string, options?: { duration?: number }) => void;
      };
    };
  }
}

if (getPlatform() === 'Woo') {
  import('./wooStyle.css');
}

const optimizely = createInstance({
  sdkKey: process.env.REACT_APP_OPTIMIZELY_SDK_KEY,
});
const queryClient = new QueryClient();

const shopDomain = getShopDomain();
const env = getEnvironment();
if (
  env === 'development' ||
  env === 'test' ||
  env === 'production' ||
  env === 'staging'
) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: env,
    integrations: [
      new CaptureConsole({
        levels: ['error'],
      }),
    ],
    release: process.env.npm_package_version,
    tracesSampleRate: 1.0,
  });

  Sentry.setTag('shop_id', shopDomain);
}

mixpanel.init(getMixPanelID());

const AnalyticsConfiguration: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { data } = useShop();
  const { update: updateIntercom } = useIntercom();

  const accountId = data?.shop?.accountId;
  useEffect(() => {
    if (accountId) {
      console.info(`Identifying user as account ID ${accountId}`);
      mixpanel.identify(accountId);
      mixpanel.people.set_once('Account ID', accountId); // Useful because Mixpanel has a bad habit of using the first distinct ID when we merge. Using ths property we can always get the latest account ID.
      mixpanel.people.set_once({ 'Sign Up Date': new Date() });
      Sentry.setUser({ username: accountId, id: shopDomain });
      updateIntercom({ userId: accountId });
    } else {
      // Either `data` hasn't loaded yet or it has but there's no account ID yet.
      // Either way, temporarily use the shop domain as the user ID.
      // Note for the second case (shop doesn't have an account ID yet), core will eventually fix this for mixpanel
      // by merging the identities when the account ID is created.
      console.info(`Identifying user as shop domain ${shopDomain}`);
      Sentry.setUser({ id: shopDomain });
      mixpanel.identify(shopDomain);
      mixpanel.people.set_once('Shop Domain', shopDomain);
      // For backwards compatibility, intercom is not set in this case
    }
  }, [accountId, updateIntercom]);

  return <>{children}</>;
};

/**
 * On Woo platform, reports back our current path to the parent window.
 */
const WooIframeAdapter: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    if (getPlatform() !== 'Woo') {
      return;
    }

    const currentPath = location.pathname;

    let search: string = '';

    if (location.search) {
      const params = new URLSearchParams(location.search);
      params.delete('wooSecret');
      params.delete('shop');
      params.delete('platform');
      search = `?${params.toString()}`;
    }

    const route = `${currentPath}${search}`;
    window.parent.postMessage(
      { type: 'pathUpdate', route: route },
      `https://${shopDomain}`
    );
  }, [location]);

  return null;
};

const Redirect = ({ to }: { to: string }) => {
  const navigate = useNavigate();

  useEffect(() => {
    navigate(to, { replace: true });
  }, [navigate, to]);

  return <></>;
};

export const useOnboardingRequired = () => {
  const accountCreated = useSession(isAccountCreated);
  const accountSessionLoaded = useSession(isAccountSessionLoaded);
  const { isLoading, data } = useShop({
    enabled: accountSessionLoaded && accountCreated,
  });
  const onboardingRequired = !data?.shop?.accountId;

  return {
    isLoading: isLoading || !accountSessionLoaded,
    isRequired: onboardingRequired || !accountCreated,
  };
};

const Root = () => {
  const location = useLocation();
  const search = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const navigate = useNavigate();

  useEffect(() => {
    const go = async () => {
      if (search) {
        const shop = search.get('shop');
        const token = search.get('id_token');
        if (shop && !token) {
          navigate(`${INSTALL_PATH}?${search.toString()}`, { replace: true });
        } else if (token) {
          navigate(`${DASHBOARD_PATH}?${search.toString()}`, {
            replace: true,
          });
        }
      }
    };
    go();
  }, [search, navigate]);

  return <LoadingPage />;
};

const OnboardingRequiredRoutes = () => {
  const { isLoading, isRequired } = useOnboardingRequired();

  if (isLoading) {
    return <LoadingPage />;
  }

  if (isRequired) {
    return <Redirect to={ONBOARDING_PATH} />;
  }

  return <Outlet />;
};

const ActivePlanRequiredRoutes = () => {
  const accountCreated = useSession(isAccountCreated);
  const accountSessionLoaded = useSession(isAccountSessionLoaded);
  const { isLoading, data } = useShop({
    enabled: accountSessionLoaded && accountCreated,
  });
  const planActive = data?.shop?.planType !== PlanType.FREE;

  if (!accountSessionLoaded || isLoading) {
    return <LoadingPage />;
  }

  if (!planActive) {
    return <Redirect to={PLAN_PATH} />;
  }

  return <Outlet />;
};

const CapabilityRequiredRoute: React.FC<{
  children: React.ReactNode;
  capability: Capability;
  redirect: string;
}> = ({ children, capability, redirect }) => {
  const { data } = useGetCapabilities();

  if (!data) {
    return <LoadingPage />;
  }

  if (data.capabilities.indexOf(capability) !== -1) {
    return <>{children}</>;
  }

  return <Redirect to={redirect} />;
};

/**
 * Everything that used to sit between <BrowserRouter> and <Routes> lives in
 * this pathless layout route so it stays inside the router context. The app
 * uses a data router (createBrowserRouter) instead of <BrowserRouter>
 * because ContextualSaveBar's useBlocker — which blocks in-app navigation
 * while a form has unsaved changes (BFS 4.1.5) — only works within a data
 * router.
 */
const RootLayout = () => (
  <OptimizelyProvider
    optimizely={optimizely}
    user={{
      id: getShopDomain(),
      attributes: {
        shopify_domain: getShopDomain(),
      },
    }}
  >
    <PolarisProvider i18n={enTranslations} linkComponent={PolarisLink}>
      <WooWrapper>
        <ErrorBoundary
          FallbackComponent={ErrorComponent}
          onError={handleGeneralError}
        >
          <AnalyticsConfiguration>
            <Frame>
              <Suspense fallback={<LoadingPage />}>
                <Outlet />
              </Suspense>
              <ToastNotification />
            </Frame>
          </AnalyticsConfiguration>
        </ErrorBoundary>
      </WooWrapper>
    </PolarisProvider>
  </OptimizelyProvider>
);

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<RootLayout />}>
      <Route path="/" element={<Root />} />
      <Route path={INSTALL_PATH} element={<Install />} />
      <Route path={REDIRECT_PATH} element={<OAuthRedirect />} />
      <Route
        element={
          <>
            <ShopifyWrapper>
              <ContextualSaveBar>
                <FinaliseChargeProvider>
                  <WooIframeAdapter />
                  <Outlet />
                </FinaliseChargeProvider>
              </ContextualSaveBar>
            </ShopifyWrapper>
          </>
        }
      >
        <Route
          path="/apps/:apiKey"
          element={<Redirect to={DASHBOARD_PATH} />}
        />
        <Route path={ONBOARDING_PATH} element={<OnboardingPage />} />
        <Route element={<OnboardingRequiredRoutes />}>
          <Route path={PLAN_PATH} element={<ConnectedPricingPage />} />
          <Route element={<ConnectedLegacyPlanRedirectRoutes />}>
            {/*
                                Back-compat: Shopify billing charges created
                                before PLAN_PATH moved to /settings/plan have
                                a return URL of /plan baked in. Keep this route
                                so those in-flight charges still land on the
                                pricing page, where FinaliseChargeProvider
                                confirms them.
                              */}
            <Route path="/plan" element={<ConnectedPricingPage />} />
            <Route path="/fixSubscription" element={<FixSubscriptionPage />} />
            <Route
              path={SELF_CREATOR_DETAILS_PATH}
              element={<SelfCreatorDetailsPage />}
            />
            <Route element={<ActivePlanRequiredRoutes />}>
              <Route path={DASHBOARD_PATH} element={<DashboardPage />} />
              <Route path="/nft" element={<Redirect to="/drops" />} />
              <Route
                path="/product-metadata/:id/:defaultType?"
                element={<MetafieldFormLoader isVariant={false} />}
              />
              <Route
                path="/variant-metadata/:id/:defaultType?"
                element={<MetafieldFormLoader isVariant={true} />}
              />
              <Route
                path="/certificate/product/:id/:defaultType?"
                element={<MetafieldFormLoader isVariant={false} />}
              />
              <Route
                path="/drops/product/:id/:defaultType?"
                element={<MetafieldFormLoader isVariant={false} />}
              />
              <Route path={SETTINGS_PATH} element={<SettingsPage />} />
              <Route
                path={CUSTOMIZATIONS_CERTIFICATE_CUSTOMIZATION_PATH}
                element={<CustomizationSettingsPage />}
              />
              <Route
                path={SETTINGS_EMAIL_GLOBAL_PATH}
                element={
                  <CapabilityRequiredRoute
                    capability={'emails.customize'}
                    redirect="/customizations"
                  >
                    <EmailGlobalCustomization />
                  </CapabilityRequiredRoute>
                }
              />
              <Route
                path={CUSTOMIZATIONS_EMAIL_COA_PATH}
                element={
                  <CapabilityRequiredRoute
                    capability={'emails.customize'}
                    redirect="/customizations"
                  >
                    <CertificateEmailPage />
                  </CapabilityRequiredRoute>
                }
              />
              <Route
                path={CUSTOMIZATIONS_EMAIL_NFT_PATH}
                element={
                  <CapabilityRequiredRoute
                    capability={'emails.customize'}
                    redirect="/customizations"
                  >
                    <NftClaimEmailPage />
                  </CapabilityRequiredRoute>
                }
              />
              <Route
                path={CUSTOMIZATIONS_EMAIL_VIEW_NFT_PATH}
                element={
                  <CapabilityRequiredRoute
                    capability={'emails.customize'}
                    redirect="/customizations"
                  >
                    <NftMintedEmailPage />
                  </CapabilityRequiredRoute>
                }
              />
              <Route
                path={ROYALTY_SPLITS_PATH}
                element={<RoyaltySplitPageLoader />}
              />
              <Route
                path={`${ROYALTY_SPLITS_PATH}/create`}
                element={<RoyaltySplitPageCreateLoader />}
              />
              <Route
                path={`${ROYALTY_SPLITS_PATH}/edit/:id`}
                element={<RoyaltySplitPageEditLoader />}
              />
              <Route path={CREATORS_PATH} element={<CreatorsPage />} />
              <Route path={TOKEN_GATES_PATH} element={<TokenGatePage />} />
              <Route path="/certificate" element={<CertificateList />} />
              <Route path={PRINTS_PATH} element={<PrintsPage />} />
              <Route
                path={`${PRINTS_PATH}/create`}
                element={<PrintsFormPage />}
              />
              <Route
                path={`${PRINTS_PATH}/edit/:id`}
                element={<EditPrintFormPage />}
              />
              <Route
                path={`${TOKEN_GATES_PATH}/create`}
                element={<TokenGateFormPage />}
              />
              <Route
                path={`${TOKEN_GATES_PATH}/edit/:id`}
                element={<EditTokenGateFormPage />}
              />
              <Route path={CONTRACTS_PATH} element={<ContractsListPage />} />
              <Route
                path={`${CONTRACTS_PATH}/create`}
                element={<CreateContractsFormPage />}
              />
              <Route
                path={`${CONTRACTS_PATH}/edit/:id`}
                element={<EditContractsFormPage />}
              />
              <Route path="/drops" element={<DropPageLoader />} />
              <Route path="/drops/create" element={<CreateDropPage />} />
              <Route path="/drops/edit/:id" element={<EditDropPage />} />
              <Route path="/reports" element={<ReportsPageLoader />} />
              <Route
                path="/reports/nft/:blockchain/:address"
                element={<ReportPageLoader />}
              />
              <Route path="/admin/:type/edit" element={<AdminLink />} />
            </Route>
          </Route>
        </Route>
        <Route path="*" element={<PageNotFound />} />
      </Route>
    </Route>
  )
);

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <GoogleTagManager>
        <IntercomProvider
          appId={getIntercomAppId()}
          autoBoot={false}
          onShow={() => {
            mixpanel.track('Customer Support Opened', {
              platform: getPlatform(),
            });
          }}
        >
          <RouterProvider router={router} />
        </IntercomProvider>
      </GoogleTagManager>
    </QueryClientProvider>
  );
}

export default App;
